import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'antd';
import { useMethods } from '@lhb/hook';
import { downloadFile } from '@lhb/func';
import { getCookie } from '@lhb/cache';
import dayjs from 'dayjs';
import { exportModelClusterPDFAsync } from '@/common/api/siteselectionmap';
import { getPDFExportStatus } from '@/common/api/networkplan';
import { usePageTitle } from '@/common/hook/usePageTitle';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { PDF_CONTAINER_CLASS, PDF_PAGE_CLASS } from '@/views/pdf/pages/ymdecisionreport/pagination';
import { DECISION_EXPORT_PERMISSION, DECISION_EXPORT_TYPE } from '../config';

/** 复用既有生成、状态和查看流程；仅新增独立业务类型与打印页面。 */
const DecisionReportExport = ({ open, detail, permissions }: { open: boolean; detail: any; permissions?: any[] }) => {
  const tenantCheck = useSelector((state: any) => state.common.tenantCheck);
  const user = useSelector((state: any) => state.common.currentUserInfo);
  const { name: menuName } = usePageTitle();
  const allowed = tenantCheck?.taskTemplateCode === 'standardAndClusterB' && !!permissions?.some((item) => item?.event === DECISION_EXPORT_PERMISSION);
  const [status, setStatus] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const versionRef = useRef(0);
  const lockRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const id = detail?.id;

  const methods = useMethods({
    clearTimer() {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = undefined;
    },
    async loadStatus(version: number) {
      try {
        // 独立轮询不参与按 URL 自动取消，避免与旧机会点 type=2 的查询互相取消。
        const result = await getPDFExportStatus({ type: DECISION_EXPORT_TYPE, id }, { needCancel: false });
        if (version !== versionRef.current) return;
        setStatus(result);
        setBusy(result?.exportStatus === 3);
        if (result?.exportStatus === 3) {
          timerRef.current = setTimeout(() => methods.loadStatus(version), 5000);
        }
      } catch {
        // 错误提示沿用既有请求层；恢复按钮，允许用户重试。
        if (version === versionRef.current) setBusy(false);
      }
    },
    async exportReport() {
      if (!open || !allowed || !id || lockRef.current || busy) return;
      lockRef.current = true;
      const version = ++versionRef.current;
      methods.clearTimer();
      setBusy(true);
      setStatus(null);
      const waterText = user?.name ? (user.mobile ? `${user.name} ${user.mobile.slice(-4)}` : user.name) : '';
      const now = dayjs();
      const name = `决策报告_${detail.name || '-'}_${now.format('YYYYMMDD_HHmm')}.pdf`;
      const query = new URLSearchParams({
        chancePointId: String(id),
        generatedAt: now.format('YYYY.MM.DD HH:mm'),
        exporterName: user?.name || '',
        waterText,
        tenantId: String(getCookie('tenantId') || ''),
        employeeId: String(getCookie('employeeId') || ''),
      });
      try {
        await exportModelClusterPDFAsync({
          id,
          type: DECISION_EXPORT_TYPE,
          name,
          menuName,
          nodeData: {
            name,
            needPageTable: false,
            options: [{
              url: [`${process.env.CONSOLE_PC_URL}/pdf/ymdecisionreport?${query.toString()}`],
              containerClass: PDF_CONTAINER_CLASS,
              childClass: PDF_PAGE_CLASS,
            }],
          },
        });
        if (version !== versionRef.current) return;
        V2Message.success('报告正在生成中，请稍后查看');
        methods.loadStatus(version);
      } catch {
        if (version === versionRef.current) setBusy(false);
      } finally {
        if (version === versionRef.current) lockRef.current = false;
      }
    },
    viewReport() {
      if (status?.exportStatus !== 2 || !status.name || !status.url) return;
      downloadFile({
        name: status.name,
        url: `${process.env.STATIC_RES_DOMAIN}/libs/preview-pdf/web/viewer.html?file=${encodeURIComponent(status.url)}&fixname=${encodeURIComponent(status.name)}`,
      });
    },
  });

  useEffect(() => {
    const version = ++versionRef.current;
    methods.clearTimer();
    lockRef.current = false;
    setStatus(null);
    setBusy(false);
    if (open && allowed && id) methods.loadStatus(version);
    return () => {
      versionRef.current += 1;
      methods.clearTimer();
      lockRef.current = false;
    };
  }, [open, allowed, id, methods]);

  if (!allowed) return null;
  return <>
    <Button onClick={methods.exportReport} loading={busy} disabled={busy}>导出 PDF</Button>
    {status?.exportStatus === 2 && status.name && status.url && <Button onClick={methods.viewReport}>查看报告</Button>}
  </>;
};

export default DecisionReportExport;
